import { ColumnsType } from 'antd/es/table';
import { TFunction } from 'i18next';
import { NavigateFunction } from 'react-router-dom';
import { ClaimListItem } from '@/types';
import { ClaimStatusTag } from '@/components/claims';
import { formatIQD } from '@/utils/claimHelpers';
import { formatDateByDayjs } from '@/utils';
import { Copy, ShieldAlert, PackageX, AlertTriangle, ExternalLink } from 'lucide-react';
import { message } from 'antd';

export const getClaimColumns = (
  t: TFunction,
  navigate: NavigateFunction,
): ColumnsType<ClaimListItem> => [
  {
    title: String(t('claim_id', { defaultValue: 'Claim ID' })),
    dataIndex: 'platform_code',
    key: 'platform_code',
    render: (_: unknown, record: ClaimListItem) => (
      <div className='flex flex-col gap-0.5'>
        <div className='flex items-center gap-1.5'>
          <span
            style={{
              fontSize: '13px',
              fontWeight: 700,
              fontFamily: 'monospace',
              color: 'rgb(57,57,60)',
            }}
          >
            {record.platform_code}
          </span>
          <Copy
            size={12}
            className='cursor-pointer text-gray-400 hover:text-gray-600 shrink-0'
            onClick={(e) => {
              e.stopPropagation();
              navigator.clipboard.writeText(record.platform_code ?? '');
              message.success(String(t('copied')));
            }}
          />
        </div>
        <span style={{ fontSize: '11px', color: 'rgb(115,123,140)' }}>
          {formatDateByDayjs(record.created_at, 'D MMMM YYYY')}
        </span>
      </div>
    ),
  },
  {
    title: String(t('order_id', { defaultValue: 'Order ID' })),
    key: 'order',
    render: (_: unknown, record: ClaimListItem) => (
      <div className='flex items-center gap-1.5'>
        <span
          style={{
            fontSize: '13px',
            fontWeight: 700,
            fontFamily: 'monospace',
            color: 'rgb(57,57,60)',
          }}
        >
          {record.order.platform_code}
        </span>
        <button
          className='p-0.5 rounded hover:bg-gray-100 transition-colors cursor-pointer bg-transparent border-0 text-gray-400 hover:text-gray-600'
          onClick={(e) => {
            e.stopPropagation();
            window.open(`/orders/${record.order.uid}`, '_blank');
          }}
          title='Open order'
        >
          <ExternalLink size={12} />
        </button>
      </div>
    ),
  },
  {
    title: String(t('type', { defaultValue: 'Type' })),
    key: 'type',
    render: (_: unknown, record: ClaimListItem) => {
      const typeConfig: Record<string, { labelKey: string; icon: JSX.Element }> = {
        damaged: {
          labelKey: 'claim_type_damaged',
          icon: <PackageX size={12} />,
        },
        partially_damaged: {
          labelKey: 'claim_type_partially_damaged',
          icon: <ShieldAlert size={12} />,
        },
        lost: {
          labelKey: 'claim_type_lost',
          icon: <AlertTriangle size={12} />,
        },
      };
      const config = typeConfig[record.type] ?? { labelKey: record.type, icon: <ShieldAlert size={12} /> };
      return (
        <span
          className='inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-semibold border border-solid bg-white'
          style={{ borderColor: 'rgb(232,230,227)' }}
        >
          {config.icon}
          {String(t(config.labelKey))}
        </span>
      );
    },
  },
  {
    title: String(t('declared_value', { defaultValue: 'Declared Value' })),
    key: 'declared_value',
    render: (_: unknown, record: ClaimListItem) => (
      <span className='text-[13px] text-[rgb(57,57,60)]'>
        {formatIQD(record.declared_value)}
      </span>
    ),
  },
  {
    title: String(t('compensation', { defaultValue: 'Compensation' })),
    key: 'compensation_amount',
    render: (_: unknown, record: ClaimListItem) => (
      <span
        className='text-[13px] font-medium'
        style={{
          color: record.compensation_amount !== null ? 'rgb(34,197,94)' : 'rgb(115,123,140)',
        }}
      >
        {formatIQD(record.compensation_amount)}
      </span>
    ),
  },
  {
    title: String(t('status', { defaultValue: 'Status' })),
    key: 'status',
    render: (_: unknown, record: ClaimListItem) => (
      <ClaimStatusTag slug={record.status.slug} />
    ),
  },
];
